import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MusicWidget } from "./MusicWidget";
import { useTranslation } from 'react-i18next';
import {
  User, Calendar, Clock, FileText, Users, CheckCircle, AlertCircle,
  ArrowRight, Star, Wallet, Briefcase, ClipboardList, MessageSquare, Award,
} from 'lucide-react';
import { getLeaveBalances, getMyLeaveEmployments, getMyProfile, getCachedProfile, getTeamOut, getPendingApprovals, getMyActivity, type MyActivityEntry } from '@/lib/hrApi';
import { annualHeadline } from '@/features/pages/time-off/balance';
import { delegationParties } from './activityNames';
import { listNotifications, markNotificationRead } from '@/lib/notificationsApi';
import { myAssignmentStatusCounts } from '@/services/workAssignmentService';
import { workItemStatusCounts } from '@/services/workService';
import { formatDateTime } from '@/lib/orgFormat';
import { useOrgFormat } from '@/lib/useOrgFormat';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { SetupBanner } from '@/features/onboarding/setup/SetupBanner';
import { useC, useDark, type C as ColorSet } from '@/features/shared/theme/kaziColors';
import { useIsMobile } from '@/features/shared/hooks/use-mobile';
import { InstallCard } from '@/features/shared/components/install/InstallCard';
import { OfficeCheckInCard } from '@/features/pages/workassignments/OfficeCheckInCard';

interface Activity { title: string; message?: string; when: string; link?: string; entityType?: string; entityId?: string; actionable?: boolean; }

// Sum the "active" (non-terminal) buckets from a status→count map — the count of open assignments /
// work items still on the person's plate.
const TERMINAL_STATUS = new Set(['completed', 'cancelled', 'canceled', 'rejected', 'archived', 'closed', 'done']);
const activeCount = (counts: Record<string, number>): number =>
  Object.entries(counts || {}).reduce((n, [k, v]) => (TERMINAL_STATUS.has(String(k).toLowerCase()) ? n : n + (Number(v) || 0)), 0);

// Map a curated audit entry to a friendly "you did X" activity row. Write requests
// map by endpoint so the user's own meaningful actions read nicely. Anything
// unrecognised returns null (dropped) so the feed never shows raw HTTP verbs, codes
// — or sign-in/sign-out noise. Routine sign-ins are deliberately NOT surfaced:
// they drown out what actually happened in the team/company. Security-relevant
// support access (impersonation) is kept.
function auditToActivity(e: MyActivityEntry): Activity | null {
  const named: Record<string, { title: string; message: string }> = {
    impersonate: { title: 'Account accessed by support', message: 'A support operator accessed your account' },
  };
  if (named[e.action]) return { title: named[e.action].title, message: named[e.action].message, when: e.timestamp };
  // Delegation lifecycle (auth RBAC audit): show PEOPLE, never ids. The backend
  // records names alongside ids (metadata.delegatorName/delegateName + entityName
  // "A → B"); delegationParties() prefers metadata, then safe legacy names, then
  // the neutral fallback — and refuses anything id-shaped (defense in depth).
  if (e.action.startsWith('rbac.delegation.')) {
    const { delegator, delegate } = delegationParties(e.entityName, e.metadata);
    const verb = e.action.split('.').pop();
    const copy: Record<string, { title: string; message: string }> = {
      create: { title: 'Delegation created', message: `You delegated responsibilities to ${delegate}` },
      revoke: { title: 'Delegation ended', message: `You ended a delegation to ${delegate}` },
      approve: { title: 'Delegation approved', message: `You approved ${delegator}'s delegation to ${delegate}` },
      reject: { title: 'Delegation rejected', message: `You rejected ${delegator}'s delegation to ${delegate}` },
      accept: { title: 'Delegation accepted', message: `You accepted a delegation from ${delegator}` },
      decline: { title: 'Delegation declined', message: `You declined a delegation from ${delegator}` },
    };
    const c = copy[verb ?? ''] ?? { title: 'Delegation updated', message: `Delegation between ${delegator} and ${delegate} changed` };
    return { title: c.title, message: c.message, when: e.timestamp, link: '/app/admin/delegations', entityType: 'delegation', entityId: e.id };
  }

  // Write requests: match the endpoint (path only) to a known self-action.
  const verb = (e.action || '').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(verb)) return null;
  const path = (e.endpoint || '').split('?')[0];
  const rules: { test: RegExp; verbs?: string[]; title: string; message: string }[] = [
    { test: /\/users\/profile\/password$/, title: 'Password changed', message: 'You changed your password' },
    { test: /\/users\/profile\/avatar$/, title: 'Profile photo updated', message: 'You updated your profile photo' },
    { test: /\/users\/profile\/preferences$/, title: 'Preferences updated', message: 'You updated your preferences' },
    { test: /\/users\/profile$/, title: 'Profile updated', message: 'You updated your profile' },
    { test: /\/users\/[^/]+\/role$/, title: 'Role changed', message: "You changed a teammate's access" },
    { test: /\/invitations\/send$/, title: 'Invitation sent', message: 'You invited a teammate' },
    { test: /\/invitations\/resend$/, title: 'Invitation resent', message: 'You resent an invitation' },
    { test: /\/invitations\//, verbs: ['delete'], title: 'Invitation cancelled', message: 'You cancelled an invitation' },
    { test: /\/auth\/mfa\/enroll/, title: 'Two-factor enabled', message: 'You set up two-factor authentication' },
    { test: /\/tenants\/[^/]+$/, verbs: ['put', 'patch'], title: 'Company settings updated', message: 'You updated company settings' },
  ];
  for (const r of rules) {
    if (r.test.test(path) && (!r.verbs || r.verbs.includes(verb))) {
      return { title: r.title, message: r.message, when: e.timestamp };
    }
  }
  return null;
}

// ── v4 design-system primitives (inline-style, theme-aware) ─────────────────
function ProgressBar({ value, max }: { value: number; max: number }) {
  return (
    <div style={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,0.2)", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min(100, max ? (value / max) * 100 : 0)}%`, background: "rgba(255,255,255,0.5)", borderRadius: 99, transition: "width 0.6s ease" }} />
    </div>
  );
}

function StatCard({ label, value, sub, progress, max, icon: Icon, tag, onClick, loading }: {
  label: string; value: string | number; sub: string;
  progress?: number; max?: number; icon?: React.ComponentType<any>; tag?: string; onClick?: () => void;
  // US-22 — while the numbers are still loading, show a placeholder instead of a
  // misleading "0 of 0" for data that actually exists.
  loading?: boolean;
}) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{ background: "linear-gradient(135deg,#F28A3A 0%,#E0720E 100%)", borderRadius: 18, padding: "22px 22px 18px", display: "flex", flexDirection: "column", gap: 12, position: "relative", overflow: "hidden", cursor: onClick ? 'pointer' : 'default', transition: 'transform 0.2s, box-shadow 0.2s', transform: onClick && hov ? 'translateY(-3px)' : 'none', boxShadow: onClick && hov ? '0 12px 28px rgba(224,114,14,0.35)' : 'none' }}>
      <div style={{ position: "absolute", top: -20, right: -20, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
      <div style={{ position: "absolute", bottom: -30, right: 20, width: 70, height: 70, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 8, letterSpacing: "0.04em" }}>{label}</p>
          {loading
            ? <span className="animate-pulse" style={{ display: "inline-block", width: 56, height: 26, borderRadius: 8, background: "rgba(255,255,255,0.25)" }} aria-hidden />
            : <p style={{ fontSize: 32, fontWeight: 800, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</p>}
        </div>
        {Icon && (
          <div style={{ width: 38, height: 38, borderRadius: 11, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={18} color="#fff" />
          </div>
        )}
      </div>
      <div>
        {loading
          ? <span style={{ display: "inline-block", width: "70%", height: 10, borderRadius: 6, background: "rgba(255,255,255,0.18)", marginBottom: 8 }} aria-hidden />
          : <p style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 8 }}>{sub}</p>}
        {!loading && progress !== undefined && max ? <ProgressBar value={progress} max={max} /> : null}
      </div>
      {tag ? (
        <span style={{ position: "absolute", top: 14, right: 14, fontSize: 9, fontWeight: 700, background: "rgba(255,255,255,0.22)", color: "#fff", padding: "3px 8px", borderRadius: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>{tag}</span>
      ) : null}
    </div>
  );
}

function QuickAction({ icon: Icon, label, color, lightBg, darkBg, onClick, C, dark, badge }: {
  icon: React.ComponentType<any>; label: string; color: string; lightBg: string; darkBg: string;
  onClick?: () => void; C: ColorSet; dark: boolean; badge?: number;
}) {
  const [hov, setHov] = useState(false);
  const bg = dark ? darkBg : lightBg;
  const showBadge = !!badge && badge > 0;
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      aria-label={showBadge ? `${label}, ${badge} unread` : label}
      style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "20px 12px", borderRadius: 14, fontFamily: "inherit", cursor: "pointer", border: `1px solid ${hov ? color + "44" : C.border}`, background: hov ? bg : C.white, transition: "all 0.2s", transform: hov ? "translateY(-2px)" : "none", boxShadow: hov ? `0 8px 20px ${color}22` : "none" }}>
      {showBadge ? (
        <span aria-hidden style={{ position: "absolute", top: 8, right: 8, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: C.red, color: "#fff", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", boxShadow: `0 0 0 2px ${hov ? bg : C.white}` }}>
          {badge > 9 ? "9+" : badge}
        </span>
      ) : null}
      <div style={{ width: 44, height: 44, borderRadius: 13, transition: "all 0.2s", background: hov ? color : bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={20} color={hov ? "#fff" : color} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: C.charcoalMid, textAlign: "center", lineHeight: 1.3 }}>{label}</span>
    </button>
  );
}

const STATUS_BADGE = (C: ColorSet) => ({
  approved: { bg: C.greenLight, color: C.greenText, dot: C.green, label: "Approved" },
  pending: { bg: C.yellowLight, color: C.yellowText, dot: C.yellow, label: "Pending" },
  rejected: { bg: C.redLight, color: C.redText, dot: C.red, label: "Rejected" },
});

function ActivityItem({ icon: Icon, iconColor, iconBg, title, desc, time, status, C, onClick }: {
  icon: React.ComponentType<any>; iconColor: string; iconBg: string;
  title: string; desc?: string; time: string; status?: "approved" | "pending" | "rejected"; C: ColorSet; onClick?: () => void;
}) {
  const sb = status ? STATUS_BADGE(C)[status] : null;
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      style={{ display: "flex", gap: 12, padding: "14px 10px", margin: "0 -10px", borderRadius: 10, borderBottom: `1px solid ${C.border}`, cursor: onClick ? 'pointer' : 'default', background: onClick && hov ? (C.creamDark) : 'transparent', transition: 'background 0.15s' }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={16} color={iconColor} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: C.charcoal, flex: 1 }}>{title}</p>
          {sb ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 600, background: sb.bg, color: sb.color, whiteSpace: "nowrap" }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: sb.dot }} />{sb.label}
            </span>
          ) : null}
        </div>
        {desc ? <p style={{ fontSize: 12, color: C.charcoalLight, lineHeight: 1.5, marginBottom: 4 }}>{desc}</p> : null}
        <p style={{ fontSize: 11, color: C.charcoalSubtle }}>{time}</p>
      </div>
    </div>
  );
}

// Status is derived first from the notification's own `actionable` flag (an item that still NEEDS
// action can never be "Approved"), then from an ORDER-SENSITIVE title match. The old code matched the
// root "approv" first, which mislabels "awaiting approval" / "needs your approval" (they contain
// "approval", not "approved") as green "Approved" — the reported bug. We now match awaiting/pending
// states before the completed "approved" state, and use the past-tense word to detect completion.
const activityVisual = (title: string, C: ColorSet, actionable?: boolean) => {
  const tl = title.toLowerCase();
  if (tl.includes('reject') || tl.includes('declin')) return { icon: AlertCircle, color: C.red, bg: C.redLight, status: 'rejected' as const };
  const awaiting = actionable === true ||
    tl.includes('awaiting') || tl.includes('needs ') || tl.includes('requires') || tl.includes('action required') ||
    tl.includes('to approve') || tl.includes('pending') || tl.includes('submitted') || tl.includes('review');
  if (awaiting) return { icon: Calendar, color: C.orange, bg: C.orangeLight, status: 'pending' as const };
  if (tl.includes('approved') || tl.includes('completed') || tl.includes('confirmed') || tl.includes('paid')) return { icon: CheckCircle, color: C.green, bg: C.greenLight, status: 'approved' as const };
  if (tl.includes('document') || tl.includes('payslip') || tl.includes('contract')) return { icon: FileText, color: C.charcoalMid, bg: C.creamDark, status: undefined };
  return { icon: Calendar, color: C.orange, bg: C.orangeLight, status: undefined };
};

export function EmployeeDashboard() {
  useOrgFormat(); // re-render Recent Activity timestamps when the org date format loads/changes
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const C = useC();
  const dark = useDark();
  const isMobile = useIsMobile();
  const { isManager, isTenantAdmin } = useRoleAccess();
  const canApprove = isManager || isTenantAdmin;

  const [leaveAvailable, setLeaveAvailable] = useState<number | null>(null);
  const [leaveEntitled, setLeaveEntitled] = useState<number | null>(null);
  const [daysTaken, setDaysTaken] = useState<number | null>(null);
  const [teamOutToday, setTeamOutToday] = useState<number | null>(null);
  const [myAssignments, setMyAssignments] = useState<number | null>(null);
  const [myWork, setMyWork] = useState<number | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<number>(0);
  const [profileGaps, setProfileGaps] = useState<number>(0);
  const [firstName, setFirstName] = useState<string>(getCachedProfile()?.first_name || '');
  const [activities, setActivities] = useState<Activity[]>([]);
  // Per-tile unread badges on the Quick Actions, sourced from the SAME notification
  // feed as the top-bar bell. We keep the unread item IDs per bucket (not just a
  // count) so opening a tile can mark exactly those items read — the badge (and the
  // bell) clear because the user acted on them.
  const [notifIds, setNotifIds] = useState<{ leave: string[]; conversation: string[]; recognition: string[]; pay: string[]; work: string[] }>({ leave: [], conversation: [], recognition: [], pay: [], work: [] });

  useEffect(() => {
    (async () => {
      try {
        const [balances, employments] = await Promise.all([
          getLeaveBalances(),
          getMyLeaveEmployments().catch(() => null),
        ]);
        // Dashboard headline = ANNUAL leave only, from the ledger the person
        // BOOKS from — the row Time Off's Annual card shows and the submission
        // checks (annualHeadline). It used to sum `available` over every
        // ANNUAL row of every ledger: Muse saw "8 annual days remaining" here
        // while Time Off said 0 and a 3-day request was refused
        // (DEF-0mvpyq10c003-513523).
        const h = annualHeadline(Array.isArray(balances) ? balances : [], employments);
        setLeaveAvailable(h.remaining);
        setLeaveEntitled(h.entitled);
        setDaysTaken(h.taken);
      } catch { /* leave service unavailable */ }
      try {
        const team = await getTeamOut(0);
        setTeamOutToday(Array.isArray(team) ? team.length : 0);
      } catch { /* leave service unavailable */ }
      // My active work assignments + work items (independent, degrade to 0 each).
      myAssignmentStatusCounts().then((c) => setMyAssignments(activeCount(c))).catch(() => setMyAssignments(0));
      workItemStatusCounts({ assignee: 'me' } as Record<string, unknown>).then((c) => setMyWork(activeCount(c))).catch(() => setMyWork(0));
      try {
        // Merge two sources into one timeline: notifications (things that
        // happened to you) + curated audit entries (things you did, e.g. sign-ins).
        const [n, audit] = await Promise.all([
          listNotifications().catch(() => ({ items: [] as any[] } as any)),
          getMyActivity(15).catch(() => [] as MyActivityEntry[]),
        ]);
        const fromNotifs: Activity[] = (n.items || []).map((x: any) => ({
          title: x.title, message: x.message, when: x.createdAt, link: x.link,
          entityType: x.entityType, entityId: x.entityId, actionable: x.actionable,
        }));
        // Quick Action badges: bucket UNREAD notification IDs by where they lead. Link
        // path is the strongest signal (it's exactly where the tile navigates); fall
        // back to the type prefix when a link is absent. Same feed as the bell.
        const buckets: { leave: string[]; conversation: string[]; recognition: string[]; pay: string[]; work: string[] } = { leave: [], conversation: [], recognition: [], pay: [], work: [] };
        for (const x of (n.items || []) as any[]) {
          if (x.isRead || !x.id) continue;
          const link = String(x.link || '').toLowerCase();
          const type = String(x.type || '').toLowerCase();
          let cat: keyof typeof buckets | null = null;
          if (/\/messages(\/|\b)/.test(link)) cat = 'conversation';
          else if (/\/my-pay(\/|\b)/.test(link)) cat = 'pay';
          else if (/\/time-off(\/|\b)/.test(link)) cat = 'leave';
          else if (/recognition/.test(link)) cat = 'recognition';
          // Work bells (work_assignment | work_reassignment | work_collaboration |
          // work_review | work_due_soon | work_overdue) all link to /app/work/{id}.
          else if (/\/app\/work(\/|\b)/.test(link)) cat = 'work';
          else if (/^conversation/.test(type)) cat = 'conversation';
          else if (/recognition/.test(type)) cat = 'recognition';
          else if (/^payslip|^pay[._]/.test(type)) cat = 'pay';
          else if (/^leave/.test(type)) cat = 'leave';
          else if (/^work_/.test(type)) cat = 'work';
          if (cat) buckets[cat].push(String(x.id));
        }
        setNotifIds(buckets);
        const fromAudit: Activity[] = audit.map(auditToActivity).filter(Boolean) as Activity[];
        // Collapse near-duplicates: the backend emits one "awaiting approval" row per run PER
        // approval level (and daily reminders don't always supersede), so a single payroll run can
        // produce a wall of rows. Keep only the most recent per entity — or, lacking an entity id,
        // per title+message. (Merged list is sorted desc below; we dedupe after sorting so "most
        // recent" wins.)
        const seen = new Set<string>();
        const merged = [...fromNotifs, ...fromAudit]
          .filter((a) => a.when)
          .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
          .filter((a) => {
            const key = a.entityType && a.entityId ? `${a.entityType}:${a.entityId}` : `${a.title}|${a.message ?? ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 8);
        setActivities(merged);
      } catch { /* notification / activity services unavailable */ }
      try {
        const me = await getMyProfile();
        if (me?.first_name) setFirstName(me.first_name);
        setProfileGaps(['phone', 'dob', 'nationality', 'national_id'].filter((k) => !me?.[k]).length);
      } catch { /* profile unavailable */ }
      if (canApprove) {
        try {
          const p = await getPendingApprovals();
          setPendingApprovals(Array.isArray(p) ? p.length : 0);
        } catch { /* not a manager / unavailable */ }
      }
    })();
  }, [canApprove]);

  const pendingActions = profileGaps + (canApprove ? pendingApprovals : 0);
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const path = (p: string) => (tenantSlug ? `/t/${tenantSlug}/app/${p}` : `/app/${p}`);
  // Leave can accrue in fractions (entitlement/12 per month), so round to 1 dp
  // for display — "39.3" not "39.33333333333333". Whole numbers stay clean.
  const fmtDays = (n: number | null | undefined): string =>
    n === null || n === undefined ? '—' : String(Math.round(n * 10) / 10);

  // Opening a Quick Action IS acting on it: optimistically clear that bucket's badge
  // and mark exactly those notifications read (best-effort), so the badge — and the
  // bell, same feed — reflect that the user has now seen them. Then navigate.
  const openTile = (cat: 'leave' | 'conversation' | 'recognition' | 'pay' | 'work', dest: string) => {
    const ids = notifIds[cat];
    if (ids.length) {
      setNotifIds((prev) => ({ ...prev, [cat]: [] }));
      ids.forEach((id) => { markNotificationRead(id).catch(() => { /* best-effort */ }); });
    }
    navigate(path(dest));
  };

  // Where an activity row navigates: its stored link (notifications carry one,
  // e.g. "/app/time-off"), else a sensible guess from the title/message.
  const resolveActivity = (act: Activity): string => {
    const rel = (act.link || '').replace(/^\/+(app|t\/[^/]+\/app)\/?/, '');
    if (rel) return path(rel);
    const tl = `${act.title} ${act.message || ''}`.toLowerCase();
    if (tl.includes('profile')) return path('personal');
    if (tl.includes('leave') || tl.includes('time off') || tl.includes('vacation') || tl.includes('anniversary') || tl.includes('birthday')) return path('time-off');
    if (tl.includes('pay') || tl.includes('payslip') || tl.includes('payroll')) return path('my-pay');
    if (tl.includes('document') || tl.includes('contract')) return path('documents');
    if (tl.includes('email') || tl.includes('mailbox')) return path('settings');
    return path('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Welcome row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: C.charcoal, letterSpacing: "-0.03em", marginBottom: 6 }}>
            {t('dashboard:welcome_back', { defaultValue: 'Welcome back' })}{firstName ? `, ${firstName}` : ''}! 👋
          </h1>
          <p style={{ fontSize: 13, color: C.charcoalLight }}>{t('dashboard:overview_message', { defaultValue: "Here's your overview for {{date}}", date: today })}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: C.white, borderRadius: 14, border: `1px solid ${C.border}` }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Star size={16} color={C.orange} />
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: C.charcoalLight, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>{t('dashboard:leave_balance', { defaultValue: 'Leave Balance' })}</p>
            <p style={{ fontSize: 15, fontWeight: 800, color: C.charcoal, letterSpacing: "-0.02em" }}>
              <span style={{ color: C.orange }}>{fmtDays(leaveAvailable)}</span> {t('dashboard:annual_days_remaining', { defaultValue: 'annual days remaining' })}
            </p>
          </div>
        </div>
      </div>

      {/* Office check-in. Renders ONLY for someone whose office is set to self
          check-in — staff checking in from a laptop land here, not on My
          Assignments where it was the only place on the web. */}
      <OfficeCheckInCard />

      {/* Install offer. Renders nothing when already running installed, when
          the browser has no install route, or for 30 days after "Maybe
          later". Account settings keeps the permanent path. */}
      <InstallCard />

      {/* Company setup checklist — admins only, auto-hides when essentials done */}
      {isTenantAdmin && <SetupBanner />}

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 16 }}>
        <StatCard label={t('dashboard:leave_balance', { defaultValue: 'Leave Balance' })} value={fmtDays(leaveAvailable)} sub={t('dashboard:annual_days_remaining', { defaultValue: 'annual days remaining' })} progress={leaveAvailable ?? 0} max={leaveEntitled ?? 0} icon={Calendar} onClick={() => navigate(path('time-off'))} loading={leaveAvailable === null || leaveEntitled === null} />
        <StatCard label={t('dashboard:my_assignments', { defaultValue: 'My Assignments' })} value={myAssignments === null ? '—' : myAssignments} sub={t('dashboard:active_assignments', { defaultValue: 'active assignments' })} icon={Briefcase} onClick={() => navigate(path('my-assignments'))} loading={myAssignments === null} />
        <StatCard label={t('dashboard:my_work', { defaultValue: 'My Work' })} value={myWork === null ? '—' : myWork} sub={t('dashboard:open_work_items', { defaultValue: 'open items assigned to you' })} icon={ClipboardList} onClick={() => navigate(path('work'))} loading={myWork === null} />
        <StatCard label={t('dashboard:pending_actions', { defaultValue: 'Pending Actions' })} value={pendingActions} sub={canApprove && pendingApprovals > 0 ? `${pendingApprovals} to approve${profileGaps ? ` · ${profileGaps} profile` : ''}` : pendingActions > 0 ? `${profileGaps} profile to complete` : 'All caught up'} icon={AlertCircle} tag={pendingActions > 0 ? 'Action needed' : undefined} onClick={() => navigate(path(canApprove && pendingApprovals > 0 ? 'leave-approvals' : 'personal'))} />
      </div>

      {/* Bottom grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 340px", gap: 20 }}>
        {/* Recent Activity */}
        <div style={{ background: C.white, borderRadius: 18, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('dashboard:recent_activity', { defaultValue: 'Recent Activity' })}</h2>
              <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2 }}>{t('dashboard:last_7_days', { defaultValue: 'Your latest requests and updates' })}</p>
            </div>
          </div>
          <div style={{ padding: "4px 22px 12px" }}>
            {activities.length === 0 ? (
              <div style={{ padding: "40px 0", textAlign: "center", color: C.charcoalLight }}>
                <Clock size={26} color={C.charcoalSubtle} style={{ margin: "0 auto 8px" }} />
                <p style={{ fontSize: 13 }}>{t('dashboard:no_recent_activity', { defaultValue: 'No recent activity' })}</p>
              </div>
            ) : activities.map((act, i) => {
              const v = activityVisual(act.title, C, act.actionable);
              return <ActivityItem key={i} C={C} icon={v.icon} iconColor={v.color} iconBg={v.bg} status={v.status} title={act.title} desc={act.message} time={formatDateTime(act.when)} onClick={() => navigate(resolveActivity(act))} />;
            })}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <MusicWidget />
          {/* Quick Actions */}
          <div style={{ background: C.white, borderRadius: 18, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            <div style={{ padding: "18px 22px 14px", borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('dashboard:quick_actions', { defaultValue: 'Quick Actions' })}</h2>
              <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2 }}>{t('dashboard:frequently_used', { defaultValue: 'Frequently used' })}</p>
            </div>
            <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <QuickAction C={C} dark={dark} icon={Calendar} label={t('dashboard:request_leave', { defaultValue: 'Request Leave' })} color={C.orange} lightBg="#FEF0E4" darkBg="rgba(242,138,58,0.13)" badge={notifIds.leave.length} onClick={() => openTile('leave', 'time-off')} />
              <QuickAction C={C} dark={dark} icon={MessageSquare} label={t('dashboard:conversations', { defaultValue: 'Conversations' })} color="#2563EB" lightBg="#DBEAFE" darkBg="rgba(37,99,235,0.13)" badge={notifIds.conversation.length} onClick={() => openTile('conversation', 'messages')} />
              <QuickAction C={C} dark={dark} icon={Award} label={t('dashboard:recognition', { defaultValue: 'Recognition' })} color="#7C3AED" lightBg="#EDE9FE" darkBg="rgba(124,58,237,0.13)" badge={notifIds.recognition.length} onClick={() => openTile('recognition', 'performance?tab=recognition')} />
              <QuickAction C={C} dark={dark} icon={Wallet} label={t('dashboard:my_pay', { defaultValue: 'My Pay' })} color="#059669" lightBg="#D1FAE5" darkBg="rgba(5,150,105,0.13)" badge={notifIds.pay.length} onClick={() => openTile('pay', 'my-pay')} />
              <QuickAction C={C} dark={dark} icon={ClipboardList} label={t('dashboard:work', { defaultValue: 'Work' })} color="#0D9488" lightBg="#CCFBF1" darkBg="rgba(13,148,136,0.13)" badge={notifIds.work.length} onClick={() => openTile('work', 'work')} />
            </div>
          </div>

          {/* Pending / Team-out summary */}
          <div style={{ background: C.white, borderRadius: 18, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            <div style={{ padding: "18px 22px 10px", borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('dashboard:team_out_today', { defaultValue: 'Team Out Today' })}</h2>
              <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2 }}>{teamOutToday ?? 0} {teamOutToday === 1 ? 'colleague' : 'colleagues'} on leave</p>
            </div>
            <div style={{ padding: "16px 22px 18px" }}>
              <div
                onClick={() => navigate(path(canApprove ? 'calendar' : 'my-calendar'))}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(path(canApprove ? 'calendar' : 'my-calendar')); } }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 12, background: dark ? C.creamDeep : C.cream, cursor: 'pointer' }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 11, background: C.orangeLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Users size={18} color={C.orange} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 20, fontWeight: 800, color: C.charcoal, lineHeight: 1 }}>{teamOutToday === null ? '—' : teamOutToday}</p>
                  <p style={{ fontSize: 11, color: C.charcoalLight }}>on approved leave today</p>
                </div>
                <ArrowRight size={16} color={C.charcoalLight} />
              </div>
              <button onClick={() => navigate(path(canApprove ? 'calendar' : 'my-calendar'))} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 12, fontSize: 12, fontWeight: 600, color: C.orange, background: C.orangeLight, border: "none", borderRadius: 9, padding: 9, cursor: "pointer", width: "100%" }}>
                <ArrowRight size={12} /> View team calendar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDashboard;
